import {
  DESLOCAMENTO_ANUAL,
  INICIO_DO_RODIZIO,
  NIVEIS_APOIO,
  PESSOAS,
  RODIZIO,
  TROCAS,
} from "../data/team";
import { feriadoEm } from "./holidays";

const DIA = 86400000;

/**
 * ── Como o rodízio é calculado ─────────────────────────────────────────────
 *
 * O plantão de um dia é uma função pura da data e do array TIME: não existe
 * estado, sorteio ou escala salva em lugar nenhum. Duas regras sustentam o
 * cálculo.
 *
 * 1. CONTÍNUO. A posição na fila é o número de dias decorridos desde
 *    INICIO_DO_RODIZIO, e não o dia do mês. Por isso a escala não reinicia no
 *    dia 1º e ninguém pega dois plantões seguidos na virada do mês — o que
 *    acontecia quando o rodízio recomeçava do primeiro nome a cada mês.
 *
 * 2. DESLOCAMENTO ANUAL. Quando o tamanho do time divide 365, o rodízio
 *    voltaria à mesma posição todo 1º de janeiro e a mesma pessoa herdaria o
 *    mesmo Natal e o mesmo 7 de setembro para sempre. Um empurrão de um nome
 *    por ano civil faz os feriados de data fixa circularem pelo time.
 *
 * Como a escala é calculada e não armazenada, mexer no array TIME vale para
 * toda a linha do tempo: tirar ou acrescentar alguém recalcula também os meses
 * já passados. É o preço de ter um lugar só para editar.
 *
 * As trocas pontuais (férias, imprevisto) entram por cima do resultado: o
 * titular continua sendo quem o rodízio apontou, e o responsável passa a ser
 * quem assumiu — as duas informações ficam visíveis na escala e a contagem da
 * distribuição segue o responsável.
 *
 * Todas as datas são tratadas e formatadas em UTC, para que horário de verão ou
 * fuso do navegador não empurrem um plantão para o dia errado.
 */

export function chaveData(ano, mes, dia) {
  return `${ano}-${String(mes + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
}

export function hojeUTC() {
  const agora = new Date();
  return {
    ano: agora.getFullYear(),
    mes: agora.getMonth(),
    dia: agora.getDate(),
  };
}

export function chaveDeHoje() {
  const { ano, mes, dia } = hojeUTC();
  return chaveData(ano, mes, dia);
}

function msDaChave(chave) {
  const [a, m, d] = chave.split("-").map(Number);
  return Date.UTC(a, m - 1, d);
}

function chaveDeMs(ms) {
  const d = new Date(ms);
  return chaveData(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Resto sempre positivo — o rodízio precisa funcionar antes da época também. */
function mod(n, m) {
  return ((n % m) + m) % m;
}

/**
 * Posição na fila para uma data: dias corridos desde o início do rodízio, mais
 * um nome por ano civil decorrido. Funciona para datas anteriores ao início
 * porque `mod` devolve resto sempre positivo.
 */
function indiceNaFila(chave, tamanho) {
  const dias = Math.round((msDaChave(chave) - msDaChave(INICIO_DO_RODIZIO)) / DIA);
  const anos = Number(chave.slice(0, 4)) - Number(INICIO_DO_RODIZIO.slice(0, 4));
  return mod(dias + DESLOCAMENTO_ANUAL * anos, tamanho);
}

/**
 * Titular do dia pelo rodízio, antes de aplicar trocas pontuais.
 * Aceita uma fila alternativa para os testes conseguirem simular entradas e
 * saídas do time sem tocar no cadastro real.
 */
export function titularDoDia(chave, fila = RODIZIO) {
  return fila[indiceNaFila(chave, fila.length)];
}

const FMT_DIA_SEMANA = new Intl.DateTimeFormat("pt-BR", {
  weekday: "short",
  timeZone: "UTC",
});
const FMT_DIA_SEMANA_LONGO = new Intl.DateTimeFormat("pt-BR", {
  weekday: "long",
  timeZone: "UTC",
});
const FMT_DATA_LONGA = new Intl.DateTimeFormat("pt-BR", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const FMT_MES_ANO = new Intl.DateTimeFormat("pt-BR", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

function limpaDiaSemana(texto) {
  return texto.replace(/\.$/, "").replace(/-feira$/, "");
}

/** Estado completo de um dia: quem atende, por quê, e o peso do dia. */
export function plantaoDoDia(chave) {
  const ms = msDaChave(chave);
  const data = new Date(ms);
  const diaSemana = data.getUTCDay();
  const titular = titularDoDia(chave);
  const troca = TROCAS[chave];
  const responsavel = troca ? troca.assume : titular;

  return {
    chave,
    data,
    dia: data.getUTCDate(),
    diaSemana,
    diaSemanaCurto: limpaDiaSemana(FMT_DIA_SEMANA.format(data)),
    diaSemanaLongo: limpaDiaSemana(FMT_DIA_SEMANA_LONGO.format(data)),
    dataLonga: FMT_DATA_LONGA.format(data),
    fimDeSemana: diaSemana === 0 || diaSemana === 6,
    feriado: feriadoEm(chave),
    titular,
    responsavel,
    pessoa: PESSOAS[responsavel],
    troca: troca ? { ...troca, titular } : null,
    apoio: (NIVEIS_APOIO[responsavel] || []).map((id) => PESSOAS[id]),
  };
}

/** Escala completa de um mês (mes: 0-11). */
export function plantaoDoMes(ano, mes) {
  const totalDias = new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate();
  const dias = [];
  for (let dia = 1; dia <= totalDias; dia++) {
    dias.push(plantaoDoDia(chaveData(ano, mes, dia)));
  }
  return dias;
}

export function rotuloMes(ano, mes) {
  const texto = FMT_MES_ANO.format(new Date(Date.UTC(ano, mes, 1)));
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * Carga por pessoa no período. É o número que responde "estou pegando mais
 * plantão que os outros?" — dias corridos, e separadamente os dias pesados
 * (fim de semana e feriado), que é onde a sensação de sobrecarga mora.
 *
 * A lista de pessoas é a fila do rodízio, mais quem tiver aparecido só por
 * troca no meio do caminho.
 */
export function distribuicao(dias) {
  if (!dias.length) return [];

  const ordem = [...RODIZIO];
  dias.forEach((d) => {
    if (!ordem.includes(d.responsavel)) ordem.push(d.responsavel);
  });

  const base = {};
  ordem.forEach((id) => {
    base[id] = {
      id,
      pessoa: PESSOAS[id],
      dias: 0,
      fds: 0,
      feriados: 0,
      pesados: 0,
      trocas: 0,
    };
  });

  dias.forEach((d) => {
    const alvo = base[d.responsavel];
    if (!alvo) return;
    alvo.dias += 1;
    if (d.fimDeSemana) alvo.fds += 1;
    if (d.feriado) alvo.feriados += 1;
    if (d.fimDeSemana || d.feriado) alvo.pesados += 1;
    if (d.troca) alvo.trocas += 1;
  });

  return ordem.map((id) => base[id]);
}

/** Carga acumulada em um ano inteiro — a leitura justa da rotação. */
export function distribuicaoDoAno(ano) {
  const dias = [];
  for (let mes = 0; mes < 12; mes++) dias.push(...plantaoDoMes(ano, mes));
  return distribuicao(dias);
}

/**
 * Verificação de sanidade da escala: ninguém pode pegar dois dias seguidos.
 * Usada nos testes e disponível para conferir depois de mexer no rodízio.
 */
export function diasConsecutivos(dias) {
  const conflitos = [];
  for (let i = 1; i < dias.length; i++) {
    if (dias[i].responsavel === dias[i - 1].responsavel) {
      conflitos.push([dias[i - 1].chave, dias[i].chave]);
    }
  }
  return conflitos;
}

/** Próximos N plantões de uma pessoa a partir de hoje. */
export function proximosPlantoes(idPessoa, quantidade = 3) {
  const inicio = msDaChave(chaveDeHoje());
  const encontrados = [];
  for (let i = 0; i < 400 && encontrados.length < quantidade; i++) {
    const plantao = plantaoDoDia(chaveDeMs(inicio + i * DIA));
    if (plantao.responsavel === idPessoa) encontrados.push(plantao);
  }
  return encontrados;
}
